import { DragEvent, ReactNode } from 'react';
import { NavigateFunction } from 'react-router-dom';

import {
  BackrsIcons,
  ContentNugget,
  NUGGET_DRAG_TYPE,
  NUGGET_THEMES,
  NuggetVariant,
  ActivityNugget,
  NuggetUXVariant,
  PanelNugget,
  Tier,
  NUGGET_VARIANTS,
  PanelNuggetActionParams,
  CareerCategory,
  ContentCategory,
  ContentCompetency,
  getNuggetImageUrl,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { DraggableNuggetData, isStructuredSession } from '@backrs_ui/grow-ui/src/models/models';
import {
  getCareerPathCategoryName,
  getCareerPathIcon,
} from '@backrs_ui/grow-ui/src/pages/CareerPath/util';
import { SuggestedPlaylistCard } from '@backrs_ui/grow-ui/src/pages/Priorities/atoms/SuggestedPlaylistCard';
import type {
  PostBookmarkMutationFn,
  DeleteBookmarkMutationFn,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { requestSessionSwitch } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';
import { store } from '@backrs_ui/grow-ui/src/redux/store';

import { PostHog } from 'posthog-js';

const BACKRS_URL = process.env.REACT_APP_BACKRS_URL;

type NuggetMapperParams = Readonly<{
  nugget: ContentNugget;
  mostRecentResponseSetTrack?: Tier;
  meta?: {
    career_categories?: CareerCategory[];
    content_categories?: ContentCategory[];
    content_competencies?: ContentCompetency[];
  };
}>;

export function nuggetMapper({
  nugget,
  mostRecentResponseSetTrack,
  meta,
}: NuggetMapperParams): NuggetUXVariant {
  const defaultTextNugget: NuggetUXVariant = {
    type: 'resource',
    title: nugget.title,
    url: nugget.external_url || undefined,
    previewImage: nugget.preview_media?.media_url || undefined,
  };

  // Helper function to get preview image for video variants
  const getNuggetImage = (nugget: ContentNugget): string | undefined => {
    const imageResult = getNuggetImageUrl({
      nugget,
      careerCategories: meta?.career_categories || [],
      getCareerPathIcon,
    });

    return imageResult?.type === 'URL' ? imageResult.url : undefined;
  };

  switch (nugget.variant) {
    case 'opportunity':
      return {
        type: 'opportunity',
        deadline: nugget.end_date || '',
        mediaUrl: nugget.media?.media_url || '',
      };

    case 'video-external':
      return nugget.external_url
        ? {
            type: 'video-external',
            url: nugget.external_url,
            previewImage: getNuggetImage(nugget),
          }
        : defaultTextNugget;

    case 'video-internal':
      return nugget.media?.media_url
        ? {
            type: 'video-internal',
            url: nugget.media.media_url,
            previewImage: getNuggetImage(nugget),
          }
        : defaultTextNugget;

    case 'article':
      return {
        type: 'article',
        articleUrl: nugget.external_url,
        title: nugget.title,
        previewImage: nugget.preview_media?.media_url,
      };

    case 'document':
      return nugget.media?.media_url
        ? {
            type: 'document',
            title: nugget.title,
            internalMediaItemUrl: nugget.media.media_url,
            externalUrl: nugget.external_url,
          }
        : defaultTextNugget;

    case 'audio':
      return nugget.external_url
        ? {
            type: 'audio',
            url: nugget.external_url,
            title: nugget.title,
            previewImage: getNuggetImage(nugget),
          }
        : defaultTextNugget;

    case 'institution':
      if (nugget.college_info) {
        return {
          type: 'institution',
          collegeInfo: nugget.college_info,
          previewImage: nugget.college_info.banner_media_url || undefined,
        };
      }

      return defaultTextNugget;

    case 'book':
      return {
        ...defaultTextNugget,
        type: 'book',
      };

    case 'competency': {
      const contentCompetency = meta?.content_competencies?.find(
        (competency) => competency.slug === nugget.slug,
      );
      const categoryName = contentCompetency
        ? meta?.content_categories?.find(
            (category) => category.id === contentCompetency.content_category_id,
          )?.title
        : undefined;

      return {
        type: 'competency',
        slug: nugget.slug,
        categoryName,
      };
    }

    case 'event':
      return {
        type: 'event',
        date: nugget.start_date || '',
        previewImage: nugget.preview_media?.media_url,
      };

    case 'profile-external':
      return {
        type: 'profile-external',
        abbreviatedName: nugget.external_profile_info?.first_name
          ? `${nugget.external_profile_info?.first_name} ${nugget.external_profile_info?.last_name}`
          : nugget.title,
        profileImageUrl:
          nugget.external_profile_info?.profile_image_url || nugget.preview_media?.media_url,
        location: `${nugget.external_profile_info?.city}, ${nugget.external_profile_info?.state}, ${nugget.external_profile_info?.country}`,
        jobTitle: nugget.external_profile_info?.job_title,
        employer: nugget.external_profile_info?.employer,
        school: nugget.external_profile_info?.school,
      };

    case 'profile-internal':
      return {
        type: 'profile-internal',
        abbreviatedName: `${nugget.profile_info?.first_name} ${nugget.profile_info?.last_name}`,
        location: nugget.profile_info?.location,
        jobTitle: nugget.profile_info?.job_title,
        role: nugget.profile_info?.role,
        employer: nugget.profile_info?.employer,
        school: nugget.profile_info?.school,
        grade: nugget.profile_info?.grade,
        graduationYear: nugget.profile_info?.graduationYear,
        enrollmentType: nugget.profile_info?.enrollmentType,
        profileImageUrl: nugget.profile_info?.profile_image || '/default.svg',
      };

    case 'scholarships':
      if (!nugget.scholarship_info) {
        return defaultTextNugget;
      }

      return {
        type: 'scholarships',
        scholarshipInfo: nugget.scholarship_info,
      };

    case 'internship':
      if (!nugget.internship_info) {
        return defaultTextNugget;
      }

      return {
        type: 'internship',
        internshipInfo: nugget.internship_info,
      };

    case 'post':
      return {
        type: 'post',
        text: nugget.description || '',
        headshotImg: nugget.post_info?.author?.profile_image || undefined,
        userAbbreviatedName: nugget.post_info?.author?.first_name
          ? `${nugget.post_info?.author.first_name} ${nugget.post_info?.author.last_name.charAt(0)}`
          : '',
        postDate: nugget.post_info?.created_at || '',
        postUrl: nugget.post_info?.id
          ? `${BACKRS_URL}/posts/${nugget.post_info?.id}/comments`
          : undefined,
      };

    case 'text-response':
      if (nugget.type === 'activity') {
        return defaultTextNugget;
      }

      return {
        type: 'text-response',
        text: nugget.description || '',
      };

    case 'career-path':
      return {
        type: 'career-path',
        title: nugget.title,
        careerPathInfo: nugget.career_path_info || null,
        ...(nugget.career_path_info && meta?.career_categories
          ? {
              careerPathIcon: getCareerPathIcon(meta?.career_categories, nugget.career_path_info),
              careerPathCategoryName: getCareerPathCategoryName(
                meta?.career_categories,
                nugget.career_path_info,
              ),
            }
          : {}),
        isDialogOpen: true,
        tier: mostRecentResponseSetTrack,
      };

    case 'career-track':
      return {
        type: 'career-track',
        careerTrackInfo: nugget.career_track_info,
      };

    case 'generated-content':
      return {
        type: 'generated-content',
        generatedContentInfo: nugget.generated_content_info,
      };

    case 'employer':
      return {
        type: 'employer',
        employerInfo: nugget.employer_info,
      };

    case 'track-assessment-result':
      return {
        type: 'track-assessment-result',
        trackAssessmentResult: nugget.track_assessment_result_info,
      };

    case 'track-assessment-unlock':
      return {
        type: 'track-assessment-unlock',
        trackAssessmentUnlock: nugget.track_assessment_unlock_info,
      };

    case 'educational-investment':
      return {
        type: 'educational-investment',
        educationalInvestment: nugget.educational_investment_info,
      };
    case 'tool':
      return {
        type: 'tool',
        toolInfo: nugget.tool_info,
      };

    case 'major':
      return {
        type: 'major',
        majorInfo: nugget.major_info,
      };

    case 'industry':
      return {
        type: 'industry',
        industryInfo: nugget.industry_info,
      };

    case 'coach':
      if (nugget.coach_info) {
        return {
          type: 'coach',
          coachInfo: nugget.coach_info,
        };
      }

      return defaultTextNugget;

    case 'deep-dive':
      if (nugget.deep_dive_info) {
        return {
          type: 'deep-dive',
          deepDiveInfo: nugget.deep_dive_info,
        };
      }

      return defaultTextNugget;

    default:
      return defaultTextNugget;
  }
}

export function determineTitleOnClick(nugget: ContentNugget): (() => void) | undefined {
  if (nugget.variant === 'scholarships') {
    const { scholarship_info } = nugget;

    if (!scholarship_info) {
      return undefined;
    }

    return () => window.open(scholarship_info.program_url, '_blank');
  }

  return undefined;
}

export function determineSubtitle(nugget: ContentNugget): string | undefined {
  if (nugget.variant === 'scholarships') {
    return nugget.scholarship_info?.donor.split(':')[0];
  }

  if (nugget.variant === 'internship') {
    return nugget.internship_info?.company;
  }

  if (nugget.variant === 'post') {
    const author = nugget.post_info?.author;
    const date = nugget.post_info?.created_at;

    if (author && date) {
      const formattedDate = new Date(date).toLocaleDateString();

      return `By ${author.first_name} ${author.last_name} • ${formattedDate}`;
    }
    if (author) {
      return `By ${author.first_name} ${author.last_name}`;
    }
  }

  if (nugget.variant === 'profile-external') {
    const profile = nugget.external_profile_info;

    if (profile?.job_title && profile?.employer) {
      return `${profile.job_title} at ${profile.employer}`;
    }
    if (profile?.job_title) {
      return profile.job_title;
    }
    if (profile?.employer) {
      return profile.employer;
    }
    // Alternative: return profile?.school || `${profile?.city}, ${profile?.state}`;
  }

  if (nugget.variant === 'event') {
    const date = nugget.start_date;

    if (date) {
      return nugget.end_date
        ? `Until ${new Date(nugget.end_date).toLocaleDateString()}`
        : undefined;
    }
  }

  if (nugget.variant === 'opportunity') {
    const deadline = nugget.end_date;

    if (deadline) {
      return `Deadline: ${new Date(deadline).toLocaleDateString()}`;
    }

    if (nugget.start_date) {
      return `Starts: ${new Date(nugget.start_date).toLocaleDateString()}`;
    }
  }

  if (nugget.variant === 'generated-content') {
    const content = nugget.generated_content_info;

    if (content?.created_at) {
      return `${content?.major_source || content?.minor_source} ${new Date(content.created_at).toLocaleDateString()}`;
    }
  }

  if (nugget.variant === 'article') {
    if (nugget.external_url) {
      try {
        const domain = new URL(nugget.external_url).hostname.replace('www.', '');

        return domain;
      } catch {
        return undefined;
      }
    }
  }

  if (nugget.variant === 'video-external') {
    if (nugget.external_url) {
      try {
        const domain = new URL(nugget.external_url).hostname.replace('www.', '');

        return domain;
      } catch {
        return undefined;
      }
    }
  }

  if (nugget.variant === 'video-internal') {
    if (nugget.createdByUser) {
      try {
        const { first_name, last_name } = nugget.createdByUser;

        return `${first_name} ${last_name}`;
      } catch {
        return undefined;
      }
    }
  }

  if (nugget.variant === 'audio') {
    if (nugget.external_url) {
      try {
        const domain = new URL(nugget.external_url).hostname.replace('www.', '');

        return domain;
      } catch {
        return undefined;
      }
    }
  }

  return undefined;
}

export function determineTitle(nugget: ContentNugget): string {
  if (nugget.variant === 'internship') {
    return `${nugget.title}`;
  }

  return nugget.title || '';
}

export function determineDescription(nugget: ContentNugget): string {
  if (nugget.type === 'activity') {
    // activities can be text responses, but we handle displaying them on their own
    return nugget.description;
  }

  const listOfNoCustomDescriptionVariants: NuggetVariant[] = [
    'text-response',
    'scholarships',
    'internship',
    'post',
    'profile-external',
  ];

  if (listOfNoCustomDescriptionVariants.includes(nugget.variant)) {
    return '';
  }

  return nugget.description;
}

export function determineCustomMenuItems(
  nugget: ContentNugget,
): { icon: BackrsIcons; text: string; onClick: () => void }[] | undefined {
  if (['scholarships', 'internship'].includes(nugget.variant)) {
    const scholarshipSubmissionLink =
      nugget.scholarship_info?.submission_link || nugget.scholarship_info?.program_url;

    const internshipSubmissionLink =
      nugget.internship_info?.application_url || nugget.internship_info?.company_website;

    if (!scholarshipSubmissionLink && !internshipSubmissionLink) {
      return undefined;
    }

    return [
      {
        icon: 'IconApply',
        text: 'Apply',
        onClick: () => {
          window.open(scholarshipSubmissionLink || internshipSubmissionLink, '_blank');
        },
      },
    ];
  }

  return undefined;
}

export const handlePostHogCapture = (nugget: ContentNugget, postHog: PostHog) => {
  /** get type and string identifier (slug) for Posthog */
  const getSlugAndType = (
    type: NuggetVariant,
  ): {
    type: 'nugget' | 'scholarship' | 'profile' | 'career-path' | 'internship' | 'generated-content';
    slug: string;
  } => {
    switch (type) {
      case 'scholarships':
        return { type: 'scholarship', slug: nugget.scholarship_info?.name || '' };

      case 'internship':
        return { type: 'internship', slug: nugget.internship_info?.company || '' };

      case 'profile-internal':
        return {
          type: 'profile',
          slug: `${nugget.profile_info?.first_name} ${nugget.profile_info?.last_name}`,
        };

      case 'profile-external':
        return {
          type: 'profile',
          slug: `${nugget.external_profile_info?.first_name} ${nugget.external_profile_info?.last_name}`,
        };

      case 'career-path':
        return { type: 'career-path', slug: nugget.career_path_info?.name || '' };

      case 'generated-content':
        return { type: 'generated-content', slug: nugget.generated_content_info?.uuid || '' };

      default:
        return { type: 'nugget', slug: nugget.slug };
    }
  };

  const { slug, type } = getSlugAndType(nugget.variant);

  postHog.capture('shared_content_nugget', {
    slug,
    id: nugget.id,
    type,
  });
};

export function determinePanelNuggetRender({
  nugget,
  onClick,
  key,
  actionParams,
  careerCategories,
  contentCategories,
  contentCompetencies,
  fitWidth,
}: {
  nugget: ContentNugget;
  onClick: (nugget: ContentNugget) => void;
  key: string;
  actionParams?: PanelNuggetActionParams;
  careerCategories?: CareerCategory[];
  contentCategories?: ContentCategory[];
  contentCompetencies?: ContentCompetency[];
  fitWidth?: boolean;
}): ReactNode {
  switch (true) {
    case nugget.type === 'activity':
      return (
        <ActivityNugget
          title={nugget.title}
          description={nugget.description}
          hasUserCompleted={false}
          onClick={() => onClick(nugget)}
          actionParams={actionParams}
          key={key}
          fitContent={fitWidth}
        />
      );
    case nugget.variant === 'scholarships':
      return (
        <PanelNugget
          title={nugget.title}
          variant={nuggetMapper({ nugget })}
          onClick={() => onClick(nugget)}
          key={key}
          actionParams={actionParams}
          fitContent={fitWidth}
        />
      );

    case nugget.variant === 'internship':
      return (
        <PanelNugget
          title={nugget.title}
          variant={nuggetMapper({ nugget })}
          onClick={() => onClick(nugget)}
          key={key}
          actionParams={actionParams}
          fitContent={fitWidth}
        />
      );
    case nugget.variant === 'generated-content':
      return (
        <PanelNugget
          title={nugget.title}
          body=""
          variant={nuggetMapper({ nugget })}
          onClick={() => onClick(nugget)}
          key={key}
          actionParams={actionParams}
          fitContent={fitWidth}
        />
      );
    case nugget.variant === 'profile-internal':
      return (
        <PanelNugget
          title={nugget.title}
          variant={nuggetMapper({ nugget })}
          onClick={() => onClick(nugget)}
          hideBody={false}
          fitContent={false}
          actionParams={actionParams}
        />
      );
    case nugget.variant === NUGGET_VARIANTS.PLAYLIST:
      return (
        <SuggestedPlaylistCard
          key={key}
          playlist={nugget.playlist_info as PriorityPlaylist}
          handlePlaylistClick={() => onClick(nugget)}
          cardWidth={286}
          cardHeight={367}
          shouldSetCardWidth={false}
          cardSize="SMALL"
        />
      );
    default:
      return (
        <PanelNugget
          title={nugget.title}
          body={nugget.description}
          variant={nuggetMapper({
            nugget,
            meta: {
              career_categories: careerCategories,
              content_categories: contentCategories,
              content_competencies: contentCompetencies,
            },
          })}
          onClick={() => onClick(nugget)}
          key={key}
          actionParams={actionParams}
          fitContent={fitWidth}
        />
      );
  }
}

export type NuggetCategoryKey =
  | 'learnAndExplore'
  | 'realPeopleRealStories'
  | 'growByDoing'
  | 'scholarships'
  | 'internships'
  | 'employers'
  | 'profiles'
  | 'externalProfiles'
  | 'careerPaths'
  | 'careerTracks'
  | 'posts'
  | 'competencies'
  | 'tools'
  | 'colleges'
  | 'generatedContent'
  | 'trackAssessments'
  | 'trackAssessmentUnlocks'
  | 'educationalInvestments'
  | 'majors'
  | 'industries'
  | 'coaches'
  | 'deepDives'
  | 'blueprints';

export const NUGGET_CATEGORY_LABELS: Record<NuggetCategoryKey, string> = {
  learnAndExplore: 'Learn & Explore',
  realPeopleRealStories: 'Real People, Real Stories',
  growByDoing: 'Grow by Doing',
  scholarships: 'Scholarships',
  internships: 'Internships',
  employers: 'Employers',
  competencies: 'Competencies',
  profiles: 'Profiles',
  externalProfiles: 'External Profiles',
  careerPaths: 'Career Paths',
  careerTracks: 'Career Tracks',
  posts: 'Posts',
  tools: 'Tools',
  colleges: 'Colleges',
  generatedContent: 'Generated Content',
  trackAssessments: 'Track Assessments',
  trackAssessmentUnlocks: 'Track Assessment Unlocks',
  educationalInvestments: 'Educational Investments',
  majors: 'Majors',
  industries: 'Industries',
  coaches: 'Coaches',
  deepDives: 'Deep Dives',
  blueprints: 'Blueprints',
};

export function getNuggetCategoryKey(nugget: ContentNugget): NuggetCategoryKey | null {
  if (nugget.variant === NUGGET_VARIANTS.SCHOLARSHIPS) return 'scholarships';
  if (nugget.variant === NUGGET_VARIANTS.INTERNSHIP) return 'internships';
  if (nugget.variant === NUGGET_VARIANTS.PROFILE_INTERNAL) return 'profiles';
  if (nugget.variant === NUGGET_VARIANTS.PROFILE_EXTERNAL) return 'externalProfiles';
  if (nugget.variant === NUGGET_VARIANTS.CAREER_PATH) return 'careerPaths';
  if (nugget.variant === NUGGET_VARIANTS.CAREER_TRACK) return 'careerTracks';
  if (nugget.variant === NUGGET_VARIANTS.POST) return 'posts';
  if (nugget.variant === NUGGET_VARIANTS.EMPLOYER) return 'employers';
  if (nugget.variant === NUGGET_VARIANTS.COMPETENCY) return 'competencies';
  if (nugget.variant === NUGGET_VARIANTS.TOOL) return 'tools';
  if (nugget.variant === NUGGET_VARIANTS.INSTITUTION && nugget.college_info) return 'colleges';
  if (nugget.variant === NUGGET_VARIANTS.GENERATED_CONTENT) return 'generatedContent';
  if (nugget.variant === NUGGET_VARIANTS.TRACK_ASSESSMENT_RESULT) return 'trackAssessments';
  if (nugget.variant === NUGGET_VARIANTS.TRACK_ASSESSMENT_UNLOCK) return 'trackAssessmentUnlocks';
  if (nugget.variant === NUGGET_VARIANTS.EDUCATIONAL_INVESTMENT) return 'educationalInvestments';
  if (nugget.variant === NUGGET_VARIANTS.MAJOR) return 'majors';
  if (nugget.variant === NUGGET_VARIANTS.INDUSTRY) return 'industries';
  if (nugget.variant === NUGGET_VARIANTS.COACH) return 'coaches';
  if (nugget.variant === NUGGET_VARIANTS.DEEP_DIVE) return 'deepDives';
  if (nugget.variant === NUGGET_VARIANTS.BLUEPRINT) return 'blueprints';

  if (nugget.content_nugget_theme === NUGGET_THEMES.LEARN_AND_EXPLORE) return 'learnAndExplore';
  if (nugget.content_nugget_theme === NUGGET_THEMES.REAL_PEOPLE_REAL_STORIES)
    return 'realPeopleRealStories';
  if (nugget.content_nugget_theme === NUGGET_THEMES.GROW_BY_DOING) return 'growByDoing';

  return null;
}

export const handleBookmark = async ({
  bookmarkId,
  postBookmark,
  deleteBookmark,
  nuggetId,
  variant,
}: {
  bookmarkId?: number;
  postBookmark: PostBookmarkMutationFn;
  deleteBookmark: DeleteBookmarkMutationFn;
  nuggetId: number;
  variant: ContentNugget['variant'];
}) => {
  if (bookmarkId) {
    // we just delete by the bookmark entry id, and not via the nugget/scholarship id
    await deleteBookmark(bookmarkId);

    return;
  }

  switch (variant) {
    case 'post':
      await postBookmark({ type: 'post', itemId: nuggetId });

      return;
    case 'scholarships':
      await postBookmark({ type: 'scholarship', itemId: nuggetId });

      return;

    case 'internship':
      await postBookmark({ type: 'internship', itemId: nuggetId });

      return;

    case 'profile-internal':
      await postBookmark({ type: 'profile', itemId: nuggetId });

      return;

    case 'profile-external':
      await postBookmark({ type: 'external-profile', itemId: nuggetId });

      return;

    case 'career-path':
      await postBookmark({ type: 'career-path', itemId: nuggetId });

      return;

    case 'career-track':
      await postBookmark({ type: 'career-track', itemId: nuggetId });

      return;

    case 'generated-content':
      await postBookmark({ type: 'generated-content', itemId: nuggetId });

      return;

    case 'track-assessment-result':
      await postBookmark({ type: 'track-assessment-result', itemId: nuggetId });

      return;

    case 'track-assessment-unlock':
      await postBookmark({ type: 'track-assessment-unlock', itemId: nuggetId });

      return;

    case 'educational-investment':
      await postBookmark({ type: 'educational-investment', itemId: nuggetId });

      return;

    case 'tool':
      await postBookmark({ type: 'tool', itemId: nuggetId });

      return;

    case 'employer':
      await postBookmark({ type: 'employer', itemId: nuggetId });

      return;

    case 'competency':
      await postBookmark({ type: 'competency', itemId: nuggetId });

      return;

    case 'institution':
      await postBookmark({ type: 'college', itemId: nuggetId });

      return;

    case 'major':
      await postBookmark({ type: 'major', itemId: nuggetId });

      return;

    case 'industry':
      await postBookmark({ type: 'industry', itemId: nuggetId });

      return;

    case 'coach':
      await postBookmark({ type: 'coach', itemId: nuggetId });

      return;

    case 'deep-dive':
      await postBookmark({ type: 'deep-dive', itemId: nuggetId });

      return;

    case 'blueprint':
      await postBookmark({ type: 'blueprint', itemId: nuggetId });

      return;

    default:
      await postBookmark({ type: 'nugget', itemId: nuggetId });
  }
};

export const startNuggetDrag = (
  event: DragEvent<HTMLDivElement>,
  nuggetData: DraggableNuggetData,
) => {
  event.dataTransfer.setData(NUGGET_DRAG_TYPE, JSON.stringify(nuggetData));
  event.dataTransfer.effectAllowed = 'copy';
  setCardDragImage(event);
};

// snapshot the card itself (not its possibly-stretched wrapper) as the drag ghost
const setCardDragImage = (event: DragEvent<HTMLDivElement>) => {
  const card = event.currentTarget.firstElementChild;

  if (card instanceof HTMLElement) {
    const rect = card.getBoundingClientRect();

    event.dataTransfer.setDragImage(card, event.clientX - rect.left, event.clientY - rect.top);
  }
};

export const handlePanelNuggetClick = (nugget: ContentNugget, navigate: NavigateFunction) => {
  if (nugget.variant === NUGGET_VARIANTS.COMPETENCY) {
    navigate(`${APP_ROUTE.COMPETENCY}/${nugget.slug}`);

    return;
  }

  if (nugget.variant === NUGGET_VARIANTS.INSTITUTION && nugget.college_info) {
    navigate(`${APP_ROUTE.UNIVERSITY}/${nugget.college_info.id}`);

    return;
  }

  if (nugget.variant === NUGGET_VARIANTS.EMPLOYER && nugget.employer_info) {
    navigate(`${APP_ROUTE.EMPLOYER}/${nugget.employer_info.slug}`);

    return;
  }

  if (nugget.variant === NUGGET_VARIANTS.SCHOLARSHIPS && nugget.scholarship_info) {
    if (nugget.scholarship_info.program_url) {
      window.open(nugget.scholarship_info.program_url, '_blank');
    } else if (nugget.scholarship_info.submission_link) {
      window.open(nugget.scholarship_info.submission_link, '_blank');
    } else {
      navigate(getNuggetPageUrl(nugget));
    }

    return;
  }

  if (nugget.variant === NUGGET_VARIANTS.CAREER_PATH) {
    navigate(`${APP_ROUTE.CAREER_PATHS}/${nugget.id}`);

    return;
  }

  if (nugget.variant === NUGGET_VARIANTS.CAREER_TRACK) {
    navigate(APP_ROUTE.MINIMUM_TRACK);

    return;
  }

  if (nugget.variant === NUGGET_VARIANTS.PROFILE_INTERNAL) {
    navigate(`${APP_ROUTE.PROFILE}/${nugget.id}`);

    return;
  }

  if (nugget.variant === NUGGET_VARIANTS.BLUEPRINT) {
    navigate(APP_ROUTE.MY_BLUEPRINT);

    return;
  }

  if (
    nugget.variant === NUGGET_VARIANTS.TRACK_ASSESSMENT_RESULT &&
    nugget.track_assessment_result_info?.uuid
  ) {
    navigate(
      APP_ROUTE.ASSESS_MY_TRACK_RESULT.replace(':uuid', nugget.track_assessment_result_info.uuid),
    );

    return;
  }

  if (
    nugget.variant === NUGGET_VARIANTS.TRACK_ASSESSMENT_UNLOCK &&
    nugget.track_assessment_unlock_info?.trackAssessmentResult?.uuid
  ) {
    navigate(
      APP_ROUTE.ASSESS_MY_TRACK_RESULT.replace(
        ':uuid',
        nugget.track_assessment_unlock_info.trackAssessmentResult.uuid,
      ),
    );

    return;
  }

  if (nugget.variant === NUGGET_VARIANTS.PROFILE_EXTERNAL && nugget.external_profile_info?.url) {
    window.open(nugget.external_profile_info.url, '_blank');

    return;
  }

  if (
    nugget.variant === NUGGET_VARIANTS.EDUCATIONAL_INVESTMENT &&
    nugget.educational_investment_info
  ) {
    navigate(
      `${APP_ROUTE.EDUCATIONAL_INVESTMENT}/${nugget.educational_investment_info.code.replace(/_/g, '-')}`,
    );

    return;
  }

  if (nugget.variant === 'tool' && nugget.tool_info) {
    const { navigation } = nugget.tool_info;

    if (navigation.type === 'INTERNAL') {
      const targetSession = nugget.tool_info.structured_session_type;

      // Structured sessions are activated in place (via a switch prompt in the
      // chat) rather than by navigating to their route.
      if (targetSession && isStructuredSession(targetSession)) {
        store.dispatch(requestSessionSwitch(targetSession));

        return;
      }

      navigate(navigation.internal_navigation_url);

      return;
    }

    if (navigation.type === 'EXTERNAL') {
      window.open(navigation.external_navigation_url, '_blank');

      return;
    }
  }

  if (nugget.variant === NUGGET_VARIANTS.MAJOR && nugget.major_info) {
    navigate(`${APP_ROUTE.MAJORS}/${nugget.major_info.id}`);

    return;
  }

  if (nugget.variant === NUGGET_VARIANTS.INDUSTRY && nugget.industry_info) {
    navigate(`${APP_ROUTE.INDUSTRIES}/${nugget.industry_info.id}`);

    return;
  }

  if (nugget.variant === NUGGET_VARIANTS.COACH && nugget.coach_info) {
    navigate(`/profile/${nugget.coach_info.id}`);

    return;
  }

  if (nugget.variant === NUGGET_VARIANTS.DEEP_DIVE && nugget.deep_dive_info) {
    navigate(`/deep-dives/${nugget.deep_dive_info.id}`);

    return;
  }

  // For all other nugget types, navigate to the nugget page
  const nuggetPageUrl = getNuggetPageUrl(nugget);

  navigate(nuggetPageUrl);
};

// Helper for mapping nugget to page url
const getNuggetPageUrl = (nugget: ContentNugget): string => {
  const baseUrl = `${APP_ROUTE.NUGGETS}`;

  switch (nugget.variant) {
    case 'scholarships':
      return `${baseUrl}?scholarship=${nugget.scholarship_info?.id}`;

    case 'internship':
      return `${APP_ROUTE.INTERNSHIP}/${nugget.internship_info?.id}`;

    case 'profile-internal':
      return `${baseUrl}?profile-in=${nugget.profile_info?.id}`;

    case 'post':
      return `${baseUrl}?post=${nugget.post_info?.id}`;

    case 'generated-content':
      return `${baseUrl}?generated-content=${nugget.generated_content_info?.uuid}`;

    case 'major':
      return `${baseUrl}/majors/${nugget.major_info?.id}`;

    case 'industry':
      return `${baseUrl}/industries/${nugget.industry_info?.id}`;

    default:
      return `${baseUrl}?nugget=${nugget.slug}`;
  }
};

export type HandleShareParams = Readonly<{
  title: string;
  text: string;
  url: string;
  /** a callback to run after the share has completed. Ex: logging to PostHog */
  afterShare?: () => void;
}>;

export async function handleShareWithNavigator(shareParams: HandleShareParams) {
  try {
    if (navigator.share) {
      const { title, text, url } = shareParams;

      await navigator.share({
        title,
        text,
        url,
      });

      if (shareParams.afterShare) {
        shareParams.afterShare();
      }
    }
  } catch (error) {
    console.log(error);
  }
}
